import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { en } from './locales/en.js';
import type { MessageKey, Messages } from './locales/en.js';
import { ru } from './locales/ru.js';
import { es } from './locales/es.js';
import { pt } from './locales/pt.js';
import { de } from './locales/de.js';
import { fr } from './locales/fr.js';
import { ar } from './locales/ar.js';

export type LocaleCode = 'en' | 'ru' | 'es' | 'pt' | 'de' | 'fr' | 'ar';

/** Same 7-language set as the landing (`apps/landing` demo-i18n bridge). */
export const LOCALES: ReadonlyArray<{ code: LocaleCode; native: string; flag: string }> = [
  { code: 'en', native: 'English', flag: '🇬🇧' },
  { code: 'ru', native: 'Русский', flag: '🇷🇺' },
  { code: 'es', native: 'Español', flag: '🇪🇸' },
  { code: 'pt', native: 'Português', flag: '🇧🇷' },
  { code: 'de', native: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', native: 'Français', flag: '🇫🇷' },
  { code: 'ar', native: 'العربية', flag: '🇸🇦' },
];

const CATALOGS: Record<LocaleCode, Messages> = { en, ru, es, pt, de, fr, ar };
const STORAGE_KEY = 'tv.locale';
const RTL_LOCALES: ReadonlySet<LocaleCode> = new Set<LocaleCode>(['ar']);

type Vars = Record<string, string | number>;

/**
 * Bases that have `.one/.few/.many/.other` variants in the catalog —
 * derived from the EN keys, so adding a new plural group is automatic.
 */
export type PluralBase = {
  [K in MessageKey]: K extends `${infer B}.one` ? (`${B}.other` extends MessageKey ? B : never) : never;
}[MessageKey];

export interface I18nState {
  locale: LocaleCode;
  setLocale: (code: LocaleCode) => void;
  /** Translate a key; `{name}` placeholders are filled from `vars`. */
  t: (key: MessageKey, vars?: Vars) => string;
  /** Pick the right plural form for `count`, e.g. plural('unit.tabs', 3) → "3 tabs". */
  plural: (base: PluralBase, count: number) => string;
  dir: 'ltr' | 'rtl';
}

const I18nCtx = createContext<I18nState | null>(null);

function isLocale(v: string | null): v is LocaleCode {
  return v != null && LOCALES.some((l) => l.code === v);
}

function detectLocale(): LocaleCode {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLocale(saved)) return saved;
  } catch {
    /* storage disabled — fall through to navigator */
  }
  const nav = typeof navigator !== 'undefined' ? navigator.language.slice(0, 2).toLowerCase() : 'en';
  return isLocale(nav) ? nav : 'en';
}

function format(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match,
  );
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<LocaleCode>(detectLocale);

  // Reflect the locale on <html>: lang for a11y/SEO, dir for RTL (Arabic).
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = RTL_LOCALES.has(locale) ? 'rtl' : 'ltr';
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      /* storage full / disabled — non-fatal */
    }
  }, [locale]);

  const setLocale = useCallback((code: LocaleCode) => setLocaleState(code), []);

  const t = useCallback(
    (key: MessageKey, vars?: Vars) => format(CATALOGS[locale][key] ?? en[key], vars),
    [locale],
  );

  const plural = useCallback(
    (base: PluralBase, count: number) => {
      const category = new Intl.PluralRules(locale).select(count);
      const catalog = CATALOGS[locale] as Record<string, string>;
      const template = catalog[`${base}.${category}`] ?? catalog[`${base}.other`] ?? '';
      return format(template, { count });
    },
    [locale],
  );

  const dir: 'ltr' | 'rtl' = RTL_LOCALES.has(locale) ? 'rtl' : 'ltr';

  const value = useMemo<I18nState>(
    () => ({ locale, setLocale, t, plural, dir }),
    [locale, setLocale, t, plural, dir],
  );

  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export function useT(): I18nState {
  const ctx = useContext(I18nCtx);
  if (!ctx) throw new Error('useT must be used within I18nProvider');
  return ctx;
}
